import React, { useState } from 'react';
import { Plus, MessageSquare, Trash2, RotateCcw, Edit3, Check, X, Calendar, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { ChatSession } from '@/api/client';

interface ChatHistorySidebarProps {
  currentSessionId?: string;
  onSessionSelect: (sessionId: string) => void;
  onNewChat: () => void;
  className?: string;
  isDrawer?: boolean;
  // Add chat history state and functions as props
  sessions: ChatSession[];
  loading: boolean;
  deleteSession: (sessionId: string) => Promise<void>;
  restoreSession: (sessionId: string) => Promise<void>;
  updateSessionTitle: (sessionId: string, title: string) => Promise<void>;
}

export function ChatHistorySidebar({
  currentSessionId,
  onSessionSelect,
  onNewChat,
  className,
  isDrawer = false,
  // Receive chat history state and functions as props
  sessions,
  loading,
  deleteSession,
  restoreSession,
  updateSessionTitle,
}: ChatHistorySidebarProps) {
  
  const [showDeleted, setShowDeleted] = useState(false);
  const [editingSession, setEditingSession] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Separate active and deleted sessions
  const activeSessions = sessions.filter(s => !s.deleted_at);
  const deletedSessions = sessions.filter(s => s.deleted_at);

  // Filter sessions based on search query
  const filteredSessions = activeSessions.filter(session =>
    session.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDeletedSessions = deletedSessions.filter(session =>
    session.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleNewChat = async () => {
    // Don't create session here - just trigger the parent's new chat handler
    // The session will be created when the user actually sends a message
    onNewChat();
  };

  const handleDeleteSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteSession(sessionId);
    } catch (error) {
      console.error('Failed to delete chat session:', error);
    }
  };

  const handleRestoreSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await restoreSession(sessionId);
    } catch (error) {
      console.error('Failed to restore chat session:', error);
    }
  };

  const handleStartEdit = (session: ChatSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSession(session.id);
    setEditTitle(session.title);
  };

  const handleSaveEdit = async (sessionId: string) => {
    try {
      await updateSessionTitle(sessionId, editTitle);
      setEditingSession(null);
    } catch (error) {
      console.error('Failed to update chat session:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingSession(null);
    setEditTitle('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return 'Today';
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div className={cn("bg-background border-r border-border flex flex-col h-full", isDrawer ? "w-full" : "w-80", className)}>
      {/* Header */}
      <div className="p-3 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Chat History</h2>
          <Button onClick={handleNewChat} size="sm" className="gap-2">
            <Plus className="h-4 w-4" />
            New Chat
          </Button>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-muted-foreground">
            Loading chat history...
          </div>
        ) : (
          <div className="p-2 space-y-2">
            {/* Active Sessions */}
            {filteredSessions.map((session) => (
              <Card
                key={session.id}
                className={cn(
                  "group cursor-pointer transition-all duration-200 hover:shadow-md",
                  currentSessionId === session.id 
                    ? "bg-primary/10 border-primary/50 shadow-sm" 
                    : "hover:bg-muted/50"
                )}
                onClick={() => onSessionSelect(session.id)}
              >
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {editingSession === session.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="h-6 text-sm"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEdit(session.id);
                              } else if (e.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSaveEdit(session.id)}
                            className="h-6 w-6 p-0"
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCancelEdit}
                            className="h-6 w-6 p-0"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <h3 className="font-medium text-sm truncate text-foreground">
                              {session.title}
                            </h3>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(session.updated_at)}
                          </p>
                        </>
                      )}
                    </div>
                    
                    {editingSession !== session.id && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => handleStartEdit(session, e)}
                          className="h-6 w-6 p-0"
                        >
                          <Edit3 className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => handleDeleteSession(session.id, e)}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Deleted Sessions Section */}
            {deletedSessions.length > 0 && (
              <div className="mt-6">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleted(!showDeleted)}
                  className="w-full justify-start text-muted-foreground hover:text-foreground mb-2"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Deleted Chats ({deletedSessions.length})
                </Button>
                
                {showDeleted && filteredDeletedSessions.map((session) => (
                  <Card
                    key={session.id}
                    className="cursor-pointer transition-all duration-200 hover:shadow-md opacity-60 hover:opacity-80"
                    onClick={() => onSessionSelect(session.id)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <h3 className="font-medium text-sm truncate text-foreground">
                              {session.title}
                            </h3>
                            <Badge variant="secondary" className="text-xs">
                              Deleted
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Deleted {formatDate(session.deleted_at!)}
                          </p>
                        </div>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => handleRestoreSession(session.id, e)}
                          className="h-6 w-6 p-0 text-green-500 hover:text-green-700"
                          title="Restore chat"
                        >
                          <RotateCcw className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Empty State */}
            {filteredSessions.length === 0 && !loading && (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">
                  {searchQuery ? 'No chats found' : 'No chat history yet'}
                </p>
                {!searchQuery && (
                  <p className="text-xs mt-1">
                    Start a conversation to see your chat history
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
